import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import FileUploadComponent from "../../../components/FileUploadComponent";
import { generateDecisionLetter } from "../../../components/certificate/DecisionLetterGenerator";
import StageService from '../../../services/stageService';


/**
 * ExpertCommentsReissueWorkflow Component
 *
 * Self-contained component that handles the complete workflow for:
 * 1. Expert Comments submission
 * 2. Re-issue Certificate decision
 * 3. Decision letter creation and submission
 * 4. Forwarding to Joint Director
 *
 * @param {Object} props - Component props
 * @param {string} props.applicationStage - Current application stage ('18b', etc.)
 * @param {number} props.assessmentTypeId - Assessment type ID
 * @param {Function} props.getOrgId - Function to get organization ID
 * @param {Function} props.getAppId - Function to get application ID
 */
const ExpertCommentsReissueWorkflow = ({
  applicationStage,
  assessmentTypeId,
  getOrgId,
  getAppId,
}) => {
  const navigate = useNavigate();

  // Internal State Management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Expert Comments State
  const [obtainExpertComments, setObtainExpertComments] = useState("");
  const [expertCommentsSubmitted, setExpertCommentsSubmitted] = useState(false);
  const [expertCommentsFile, setExpertCommentsFile] = useState(null);

  // Re-issue Certificate State
  const [reissueCertificate, setReissueCertificate] = useState("");
  const [reissueCertificateSubmitted, setReissueCertificateSubmitted] =
    useState(false);
  const [decisionProcessType, setDecisionProcessType] = useState("");
  const [expertCommentsFileForProcess, setExpertCommentsFileForProcess] =
    useState(null);

  // Decision State
  const [approvalProcessStarted, setApprovalProcessStarted] = useState(false);
  const [subject, setSubject] = useState("");
  const [decision, setDecision] = useState("");
  const [decisionDate, setDecisionDate] = useState("");
  const [decisionLetterId, setDecisionLetterId] = useState(null);
  const [decisionLetterData, setDecisionLetterData] = useState(null);

  // Dialog States
  const [approvalPasswordDialogOpen, setApprovalPasswordDialogOpen] =
    useState(false);
  const [approvalPassword, setApprovalPassword] = useState("");
  const [showSendToDirectorModal, setShowSendToDirectorModal] = useState(false);
  const [selectedDirector, setSelectedDirector] = useState("0");
  const [directorPassword, setDirectorPassword] = useState("");
  const [directorList, setDirectorList] = useState([]);
  const [loadingDirectors, setLoadingDirectors] = useState(false);

  // Medical lab details state
  const [medicalLabDetails, setMedicalLabDetails] = useState({
    ContactPerson: "",
    ContactPersonMobileNo: "",
    ContactEmail: "",
    applicationno: "",
    Address: "",
    ExtAddress: "",
    MailBy: "",
    LaboratoryName: "",
    DoName: "",
    DoEmail: "",
  });

  // File Upload Key
  const [fileUploadKey, setFileUploadKey] = useState(0);

  // Helper function to create file object
  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`,
      originalName: fileName,
    };
  };

  // Helper function to check if ReactQuill content is empty
  const isDecisionEmpty = (content) => {
    if (!content) return true;
    const text = content.replace(/<[^>]*>/g, "").trim();
    return text.length === 0;
  };

  // Fetch medical lab details for decision letter
  const fetchMedicalLabDetails = async (appId, orgId) => {
    try {
      // Make parallel API calls to get application details and lab head details
      const [appDetailsResponse, labHeadResponse, doResponse] =
        await Promise.all([
          // Get application details (ApplicationNo, LaboratoryName, etc.)
          axios.get(
            `${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`,
          ),
          // Get lab head/contact details (mode=27)
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: {
              mode: 27,
              appId: appId,
            },
          }),
          // Get DO details (mode=31)
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: {
              mode: 31,
              appId: appId,
            },
          }),
        ]);

      // Extract data from responses
      const appData = appDetailsResponse.data?.data || {};
      const labHeadData = labHeadResponse.data?.data?.[0] || {};
      const doData = doResponse.data?.data?.[0] || {};

      // Set medical lab details
      setMedicalLabDetails({
        ContactPerson: labHeadData.name || "",
        ContactPersonMobileNo: labHeadData.mobile || "",
        ContactEmail: labHeadData.email || "",
        applicationno: appData.ApplicationNo || "",
        Address: labHeadData.address || "",
        ExtAddress:
          `${labHeadData.city || ""} ${labHeadData.state || ""} ${labHeadData.pincode || ""}`.trim(),
        MailBy: "",
        LaboratoryName: appData.LaboratoryName || "",
        DoName: doData.name || "N/A",
        DoEmail: doData.email || "N/A",
      });
    } catch (error) {
      console.error("Error fetching medical lab details:", error);
    }
  };

  // Fetch medical lab details on mount
  useEffect(() => {
    const appId = getAppId();
    const orgId = getOrgId();

    if (appId && orgId) {
      fetchMedicalLabDetails(appId, orgId);
    }
  }, [getAppId, getOrgId]);

  // Fetch existing expert comment details on mount
  useEffect(() => {
    const fetchExpertCommentDetails = async () => {
      try {
        const appId = getAppId();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/expert-comment/${appId}`,
        );

        if (response.data.success && response.data.data) {
          const data = response.data.data[0];
          setObtainExpertComments(data.Is_ExpertComment?.toString() || "");
          if (data.Is_ExpertComment) {
            setExpertCommentsSubmitted(true);
          } else {
            setExpertCommentsSubmitted(false);
          }
          if (data.ExpertCommentFile) {
            setExpertCommentsFile(createFileObject(data.ExpertCommentFile));
          }
        }
      } catch (error) {
        console.error("Error fetching expert comment details:", error);
      }
    };

    fetchExpertCommentDetails();
  }, [getAppId]);

  // Fetch existing re-issue certificate details when expert comments are submitted
  useEffect(() => {
    if (!expertCommentsSubmitted) return;

    const fetchReissueCertificateDetails = async () => {
      try {
        const appId = getAppId();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/reissue-certificate/${appId}`,
        );

        if (response.data.success && response.data.data) {
          const data = response.data.data[0];
          setReissueCertificate(data.Is_Re_IssueCertificate?.toString() || "");
          setDecisionProcessType(data.DecisionProceedThrough?.toString() || "");
          if (data.Is_Re_IssueCertificate) {
            setReissueCertificateSubmitted(true);
            setApprovalProcessStarted(true);
          } else {
            setReissueCertificateSubmitted(false);
            setApprovalProcessStarted(false);
          }

          if (data.ExpertCommentFile) {
            setExpertCommentsFileForProcess(
              createFileObject(data.ExpertCommentFile),
            );
          }
        }
      } catch (error) {
        console.error("Error fetching re-issue certificate details:", error);
      }
    };

    fetchReissueCertificateDetails();
  }, [expertCommentsSubmitted, getAppId]);

  // Fetch existing decision letter when approval process starts
  useEffect(() => {
    if (!approvalProcessStarted) return;

    const fetchDecisionLetter = async () => {
      try {
        setLoading(true);
        const appId = getAppId();
        const userTypeId = localStorage.getItem("userTypeId");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/decision-letters/${appId}/${userTypeId}`,
        );

        if (response.data.success && response.data.data) {
          const letterData = response.data.data;
          setDecisionLetterData(letterData);
          setDecisionLetterId(letterData.id);
          setDecision(letterData.body || letterData.Body || "");
          setSubject(
            letterData.Subject || "",
          );
          setDecisionDate(letterData.DecisionDate || "");
          console.log("Decision Letter loaded:", letterData);
        } else {
          setDecisionLetterData(null);
          setDecisionLetterId(null);
          setDecision("");
          setSubject("");
          setDecisionDate("");
        }
      } catch (error) {
        console.error("Error fetching decision letter:", error);
        setDecisionLetterData(null);
        setDecisionLetterId(null);
        setDecision("");
        setSubject("");
        setDecisionDate("");
      } finally {
        setLoading(false);
      }
    };

    fetchDecisionLetter();
  }, [approvalProcessStarted, getAppId]);
  // Handle Expert Comments Submission
  const handleExpertCommentsSubmit = async () => {
    if (obtainExpertComments === "1" && !expertCommentsFile) {
      setError("Please upload expert comments document");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const appId = getAppId();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/expert-comment`,
        {
          AppId: appId,
          Is_ExpertComment: obtainExpertComments,
          ExpertCommentFile: expertCommentsFile?.fileName || "",
          HeadName: "",
          Designation: "",
          orgid: localStorage.getItem("orgId"),
          clientIp: "",
        },
      );

      console.log("Expert Comment Save Response:", response.data);

      if (response.data.success) {
        setExpertCommentsSubmitted(true);
        setSuccess("Expert comment saved successfully");
      } else {
        setError("Failed to save expert comment");
      }
    } catch (err) {
      console.error("Error saving expert comment:", err);
      setError("Failed to save expert comment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Re-issue Certificate Submission
  const handleReissueCertificateSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const appId = getAppId();
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/reissue-certificate`,
        {
          appid: appId,
          Is_Re_IssueCertificate: parseInt(reissueCertificate) || 0,
          DecisionProceedThrough: decisionProcessType || "",
          ExpertCommentFile: expertCommentsFileForProcess?.fileName || "",
          orgid: localStorage.getItem("orgId"),
        },
      );

      console.log("Re-issue Certificate Save Response:", response.data);

      if (response.data.success) {
        setReissueCertificateSubmitted(true);
        setApprovalProcessStarted(true);
        setSuccess("Re-issue certificate details saved successfully");
      } else {
        setError("Failed to save re-issue certificate details");
      }
    } catch (err) {
      console.error("Error saving re-issue certificate:", err);
      setError(
        "Failed to save re-issue certificate details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Password Verification for Approval
  const handleApprovalPasswordVerification = async () => {
    if (!approvalPassword.trim()) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      setApprovalPasswordDialogOpen(false);
      setApprovalPassword("");
      handleDecisionSubmission();
    } catch (err) {
      console.error("Error verifying password:", err);
      setError("Invalid password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Final Decision Submission/Update
  const handleDecisionSubmission = async () => {
    if (isDecisionEmpty(decision)) {
      setError("Please enter a decision");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const appId = getAppId();
      const orgId = localStorage.getItem("orgId");

      let response;

      if (decisionLetterId) {
        // Update existing decision letter
        console.log("Updating decision letter ID:", decisionLetterId);
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/decision-letter`,
          {
            id: decisionLetterId,
            appid: appId,
            Subject: subject,
            Body: decision,
            orgid: orgId,
            Remarks: decisionLetterData?.Remarks || "",
            Type: decisionLetterData?.Type || "Accept",
            ContactPerson: medicalLabDetails.ContactPerson || "",
            ContactMobile: medicalLabDetails.ContactPersonMobileNo || "",
            ContactEmail: medicalLabDetails.ContactEmail || "",
            ApplicationNo: medicalLabDetails.applicationno || "",
            address: medicalLabDetails.Address || "",
            Extaddress: medicalLabDetails.ExtAddress || "",
            LabName: medicalLabDetails.LaboratoryName || "",
          },
        );
      } else {
        // Create new decision letter
        console.log("Creating new decision letter");
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/decision-letter`,
          {
            appid: appId,
            categoryid: assessmentTypeId,
            Type: "Accept",
            ContactPerson: medicalLabDetails.ContactPerson || "",
            ContactMobile: medicalLabDetails.ContactPersonMobileNo || "",
            ContactEmail: medicalLabDetails.ContactEmail || "",
            ApplicationNo: medicalLabDetails.applicationno || "",
            address: medicalLabDetails.Address || "",
            Extaddress: medicalLabDetails.ExtAddress || "",
            MailBy: medicalLabDetails.MailBy || "",
            LabName: medicalLabDetails.LaboratoryName || "",
            orgid: orgId,
            Subject: subject,
            Body: decision,
            Remarks: "",
          },
        );
      }

      if (response.data.success) {
        setSuccess(
          decisionLetterId
            ? "Decision updated successfully"
            : "Decision submitted successfully",
        );
        // Refresh decision letter data
        const appId = getAppId();
        const fetchResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/decision-letters/${appId}`,
        );

        if (fetchResponse.data.success && fetchResponse.data.data) {
          const letterData = fetchResponse.data.data;
          setDecisionLetterData(letterData);
          setDecisionLetterId(letterData.id);
          setDecision(letterData.Body || "");
          setSubject(
            letterData.Subject || "",
          );
          setDecisionDate(letterData.DecisionDate || "");
        }
      } else {
        setError(
          decisionLetterId
            ? "Failed to update decision"
            : "Failed to submit decision",
        );
      }
    } catch (err) {
      console.error("Error with decision:", err);
      setError("Failed to process decision. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle View Decision Letter - Generate PDF
  const handleViewDecisionLetter = async () => {
    try {
      setLoading(true);
      toast.info("Generating decision letter...");

      const letterData = {
        applicationNo: medicalLabDetails.applicationno || "N/A",
        letterDate: decisionDate
          ? dayjs(decisionDate, "MMM DD YYYY h:mmA").format("DD/MM/YYYY")
          : "N/A",
        contactPerson: medicalLabDetails.ContactPerson || "N/A",
        laboratoryName: medicalLabDetails.LaboratoryName || "N/A",
        address: medicalLabDetails.Address || "N/A",
        extAddress: medicalLabDetails.ExtAddress || "N/A",
        mobile: medicalLabDetails.ContactPersonMobileNo || "N/A",
        email: medicalLabDetails.ContactEmail || "N/A",
        subject: subject || "N/A",
        body: decision || "N/A",
        doName: medicalLabDetails.DoName || "N/A",
        doEmail: medicalLabDetails.DoEmail || "N/A",
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateDecisionLetter(letterData);

      toast.success("Decision letter downloaded successfully");
    } catch (err) {
      console.error("Error generating decision letter:", err);
      toast.error("Failed to generate decision letter");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Directors/CEO List
  const fetchDirectorsList = async () => {
    try {
      setLoadingDirectors(true);
      const appId = getAppId();
      const response = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/ceolist`,
        {
          params: {
            appid: appId,
            usertypeid: 9, // Joint Director user type
          },
        },
      );

      if (response.data && response.data.ceo) {
        setDirectorList(response.data.ceo);
      } else {
        setDirectorList([]);
      }
    } catch (error) {
      console.error("Error fetching directors list:", error);
      toast.error("Error loading directors list");
      setDirectorList([]);
    } finally {
      setLoadingDirectors(false);
    }
  };

  // Handle Send to Joint Director
  const handleSendToDirector = async () => {
    if (selectedDirector === "0") {
      toast.warning("Please select a Joint Director");
      return;
    }

    if (!directorPassword.trim()) {
      toast.warning("Please enter your password");
      return;
    }

    if (!decisionLetterId) {
      toast.warning("Please submit the decision letter first");
      return;
    }

    setLoadingDirectors(true);

    try {
      const localOrgId = localStorage.getItem("orgId");
      const appId = getAppId();
      const orgId = getOrgId();

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localOrgId,
          password: directorPassword,
        },
      );

      if (!passwordResponse.data.success) {
        toast.error("Invalid password. Please try again.");
        return;
      }

      // Send decision letter to joint director
      const payload = {
        id: decisionLetterId,
        appid: appId,
        orgid: localOrgId,
        sentto: selectedDirector,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/send-to-director`,
        payload,
      );

      if (!response.data.success) {
        toast.error("Failed to forward to Joint Director. Please try again.");
        return;
      }
      await StageService.updateApplicationStage(orgId, appId,'3b');
      toast.success("Application forwarded to Joint Director successfully");
      setShowSendToDirectorModal(false);
      setSelectedDirector("0");
      setDirectorPassword("");

      // Redirect after success
      setTimeout(() => {
        navigate("/main/do/dashboard");
      }, 2000);
    } catch (err) {
      console.error("Error forwarding to Joint Director:", err);
      toast.error(
        err.response?.data?.message ||
          "Failed to forward to Joint Director. Please try again.",
      );
    } finally {
      setLoadingDirectors(false);
    }
  };

  return (
    <Box>
      {/* Success Message */}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

       <Typography 
          variant="h6"
          sx={{ mb: 3, fontWeight: 700, color: "#00024c" }}
        >
          Other Activities during the accreditation cycle (Till Validity) Decision
        </Typography>

      {/* Rejection Status Display - Stage 3b */}
      {applicationStage==='3b' && decisionLetterData && decisionLetterData.IsApproved === 2 && (
        <Alert
          severity="error"
          icon={<CancelIcon fontSize="large" />}
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: "#ffebee",
            border: "2px solid #f44336",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(244, 67, 54, 0.2)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f", mb: 1 }}>
            Decision Rejected
          </Typography>
          <Typography variant="body1" sx={{ color: "#c62828", mb: 2 }}>
            The decision has been rejected by the Joint Director.
          </Typography>
          <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffffff", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#00024c", mb: 1 }}>
              Rejection Remarks:
            </Typography>
            <Typography variant="body2" sx={{ color: "#424242", whiteSpace: "pre-wrap" }}>
              {decisionLetterData.ApproveRemarks || "No remarks provided"}
            </Typography>
          </Box>
        </Alert>
      )}
 
      {/* Expert Comments Section */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}>
        <FormControl
          component="fieldset"
          fullWidth
          disabled={expertCommentsSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)}
        >
          <FormLabel
            component="legend"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: (expertCommentsSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) ? "#999" : "#00024c",
            }}
          >
            Do you wish to obtain Expert Comments?
          </FormLabel>
          <RadioGroup
            row
            value={obtainExpertComments}
            onChange={(e) => setObtainExpertComments(e.target.value)}
          >
            <FormControlLabel value="1" control={<Radio />} label="Yes" />
            <FormControlLabel value="0" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {/* Show file upload when Yes is selected */}
        {obtainExpertComments === "1" && (
          <Box sx={{ mt: 2 }}>
            <FileUploadComponent
              key={`expertComments-${fileUploadKey}`}
              fieldName="expertCommentsDocument"
              label="Upload Expert Comments Document"
              orgId={getOrgId()}
              section="expert-comments"
              accept=".pdf,.png,.jpg,.jpeg"
              maxSize={5}
              onChange={(fieldName, fileData) => {
                setExpertCommentsFile(fileData);
              }}
              initialFile={expertCommentsFile}
              required={false}
              disabled={expertCommentsSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)}
            />
          </Box>
        )}

        {/* Submit Button */}
        {obtainExpertComments && (!expertCommentsSubmitted || (applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExpertCommentsSubmit}
              disabled={loading}
              sx={{ px: 4, py: 1.5 }}
            >
              {loading ? <CircularProgress size={20} /> : "Submit"}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Re-issue Certificate Section - Show only after Expert Comments submission */}
      {expertCommentsSubmitted && (
        <Box>
          <Paper sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}>
            <FormControl
              component="fieldset"
              fullWidth
              disabled={reissueCertificateSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)}
            >
              <FormLabel
                component="legend"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: (reissueCertificateSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) ? "#999" : "#00024c",
                }}
              >
                Do you wish to Re-issue the Accreditation Certificate?
              </FormLabel>
              <RadioGroup
                row
                value={reissueCertificate}
                onChange={(e) => {
                  setReissueCertificate(e.target.value);
                  setDecisionProcessType(""); // Reset decision process when changing
                }}
              >
                <FormControlLabel value="1" control={<Radio />} label="Yes" />
                <FormControlLabel value="0" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            {/* Show Decision Process when Yes is selected */}
            {reissueCertificate === "1" && (
              <Box sx={{ mt: 2 }}>
                <FormControl
                  component="fieldset"
                  fullWidth
                  disabled={reissueCertificateSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: (reissueCertificateSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) ? "#999" : "#00024c",
                    }}
                  >
                    Decision Process Through
                  </FormLabel>
                  <RadioGroup
                    value={decisionProcessType}
                    onChange={(e) => setDecisionProcessType(e.target.value)}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Through Expert Comments"
                    />
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label="Through Supplementary Visit"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Show file upload when Through Expert Comments is selected */}
                {decisionProcessType === "1" && (
                  <Box sx={{ mt: 2 }}>
                    <FileUploadComponent
                      key={`expertCommentsProcess-${fileUploadKey}`}
                      fieldName="expertCommentsProcessDocument"
                      label="Upload Expert Comments for Process"
                      orgId={getOrgId()}
                      section="expert-comments-process"
                      accept=".pdf,.png,.jpg,.jpeg"
                      maxSize={5}
                      onChange={(fieldName, fileData) => {
                        setExpertCommentsFileForProcess(fileData);
                      }}
                      initialFile={expertCommentsFileForProcess}
                      required={false}
                      disabled={reissueCertificateSubmitted && !(applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Show Proceed for Approval button when reissueCertificate is selected and not submitted */}
            {reissueCertificate && (!reissueCertificateSubmitted || (applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReissueCertificateSubmit}
                  disabled={loading}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Proceed for Approval"
                  )}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Decision Input Field - Show after clicking Proceed for Approval */}
          {approvalProcessStarted  && (
            <Box>
              <Paper sx={{ p: 3, mb: 1, backgroundColor: "#f8f9fa" }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 3, fontWeight: 600, color: "#00024c" }}
                >
                  Decision
                </Typography>
                <TextField
                  fullWidth
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  variant="outlined"
                  InputProps={{ readOnly: (applicationStage === "18" || applicationStage === "18b" || (applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) ? false : true }}
                  sx={{ mb: 3, backgroundColor: "#ffffff" }}
                />
                <ReactQuill
                  theme="snow"
                  value={decision}
                  onChange={setDecision}
                  readOnly={(applicationStage === "18" || applicationStage === "18b" || (applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) ? false : true}
                  style={{
                    height: 250,
                    marginBottom: 50,
                    backgroundColor: "#ffffff",
                  }}
                  placeholder="Enter your decision here..."
                />
              </Paper>

              {/* Submit Button */}
              {(applicationStage === "18" || applicationStage === "18b" || (applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) && (
                <Box sx={{ textAlign: "center", mb: 3, mt: 3 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CheckCircleIcon />
                      )
                    }
                    onClick={() => setApprovalPasswordDialogOpen(true)}
                    disabled={loading || isDecisionEmpty(decision)}
                    sx={{
                      px: 5,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {loading
                      ? "Submitting..."
                      : decisionLetterId
                        ? "Update"
                        : "Submit"}
                  </Button>
                </Box>
              )}

              {/* View Decision Letter and Forward to JD Buttons */}
              {((applicationStage === "18" || applicationStage === "18b" || (applicationStage === '3b' && decisionLetterData && decisionLetterData.IsApproved === 12)) && decisionLetterId ) && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewDecisionLetter}
                    disabled={loading || isDecisionEmpty(decision)}
                    sx={{
                      px: 5,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    View Decision Letter
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={() => {
                      setShowSendToDirectorModal(true);
                      fetchDirectorsList();
                    }}
                    disabled={loading || isDecisionEmpty(decision)}
                    sx={{
                      px: 5,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    Forward to JD
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Approval Password Dialog */}
      <Dialog
        open={approvalPasswordDialogOpen}
        onClose={() => !loading && setApprovalPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify Password for Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Enter your password to proceed with the approval process.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={approvalPassword}
            onChange={(e) => setApprovalPassword(e.target.value)}
            disabled={loading}
            error={!!error}
            helperText={error}
            onKeyPress={(e) => {
              if (e.key === "Enter" && approvalPassword.trim()) {
                handleApprovalPasswordVerification();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApprovalPasswordDialogOpen(false);
              setApprovalPassword("");
              setError(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprovalPasswordVerification}
            variant="contained"
            disabled={loading || !approvalPassword.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Verifying..." : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send To Joint Director Modal */}
      <Dialog
        open={showSendToDirectorModal}
        onClose={() => setShowSendToDirectorModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 450,
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <DialogTitle sx={{ p: 0, color: "white" }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Send To Joint Director
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Select joint director and verify with your password
            </Typography>
          </DialogTitle>
        </Box>
        <DialogContent sx={{ mt: 3 }}>
          <FormControl
            fullWidth
            required
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 0 0 2px rgba(102, 126, 234, 0.1)",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 2px rgba(102, 126, 234, 0.2)",
                },
              },
            }}
          >
            <InputLabel>Select Joint Director</InputLabel>
            <Select
              value={selectedDirector}
              onChange={(e) => setSelectedDirector(e.target.value)}
              label="Select Joint Director"
              disabled={loadingDirectors}
            >
              <MenuItem value="0">--Select Joint Director--</MenuItem>
              {directorList.map((director) => (
                <MenuItem key={director.id} value={director.id}>
                  {director.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="password"
            label="Enter Your Password to Continue"
            value={directorPassword}
            onChange={(e) => setDirectorPassword(e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 0 0 2px rgba(102, 126, 234, 0.1)",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 2px rgba(102, 126, 234, 0.2)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => {
              setShowSendToDirectorModal(false);
              setSelectedDirector("0");
              setDirectorPassword("");
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendToDirector}
            disabled={loadingDirectors}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
              },
            }}
          >
            {loadingDirectors ? "Loading..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpertCommentsReissueWorkflow;
